import { useTranslation } from 'react-i18next';
import { useEffect, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ShipmentResponse, DriverProfileResponse } from '../../api/types';
import {
  adminAssignDriver,
  lookupDriverByPhone,
  getUserOverview,
  type DriverLookupResponse,
} from '../../api/endpoints/admin';
import { useMutationHook } from '../../hooks/useMutationHook';
import { INDIAN_MOBILE_ERROR, isValidNewIndianMobile } from '../../utils/phone';
import { isPhoneInvalidIndia } from '../../api/dnmFailure';
import { PhoneInput } from '../../components/ui/PhoneInput';
import styles from '../../components/domain/FormModal.module.css';
import kycStyles from './AssignDriverModal.module.css';
import { DialogShell } from '../../components/ui/DialogShell';
import { useDialogDismiss, useIsDirty } from '../../components/ui/dialogDismiss';
import { formatRoute } from '../../utils/routeLabel';
import { Link } from 'react-router-dom';
import { Notice } from '../../components/ui/Notice';
import { gateFor, kycGate, readinessMissing } from './kycGates';
import { DialogTitleRow } from '../../help/DialogTitleRow';

interface Props {
  shipment: ShipmentResponse;
  onClose: () => void;
  initialMode?: 'edit_details' | 'change_driver';
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'D';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Pre-fills stored driver numbers (any format) as 10 national digits. */
function sanitizePhoneDigits(val: string): string {
  let digits = val.replace(/[^0-9]/g, '');
  if (digits.startsWith('91') && digits.length > 10) {
    digits = digits.slice(2);
  } else if (digits.startsWith('0') && digits.length > 10) {
    digits = digits.slice(1);
  }
  return digits.slice(0, 10);
}

export function AssignDriverModal({ shipment, onClose, initialMode }: Props) {
  const { t } = useTranslation();
  // Extract initial values from existing assignment if present
  const existingAssignment = shipment.assignment;
  const initialRawPhone = existingAssignment?.driverPhone || '';
  const initialPhoneDigits = sanitizePhoneDigits(initialRawPhone);
  const initialName = existingAssignment?.driverName || '';
  const initialVehicle = (existingAssignment?.vehicleRegistrationNumber || '').toUpperCase();
  const initialVehicleId = existingAssignment?.vehicleId ? String(existingAssignment.vehicleId) : '';

  const [activeMode, setActiveMode] = useState<'edit_details' | 'change_driver'>(
    initialMode || (existingAssignment ? 'edit_details' : 'change_driver'),
  );
  const [phoneInput, setPhoneInput] = useState(
    initialMode === 'change_driver' && existingAssignment ? '' : initialPhoneDigits,
  );
  const [driverName, setDriverName] = useState(initialName);
  const [vehicleReg, setVehicleReg] = useState(initialVehicle);
  const [vehicleIdInput, setVehicleIdInput] = useState(initialVehicleId);
  const [isVehicleAutoFilled, setIsVehicleAutoFilled] = useState(!!initialVehicle);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  // Set together with the driver-overview prefill so it does not count as an edit.
  const [overviewApplied, setOverviewApplied] = useState(false);
  const [assignedData, setAssignedData] = useState<{
    phone: string;
    vehicle: string;
    name: string;
    link: string | null;
    /** Driver items still not verified (R8: warn only); empty when ready or unknown. */
    notReady: string[];
  } | null>(null);
  // R18: AWARDED → ASSIGNED blocks on the partner's KYC (no ops override); edits on ASSIGNED+ are not gated.
  const partnerGate = gateFor(kycGate(shipment), 'PARTNER');
  const partnerBlocked = partnerGate?.effect === 'BLOCKS_ASSIGN';

  const cleanDigits = phoneInput.replace(/[^0-9]/g, '');
  // Any 10 digits are looked up so an existing driver on a legacy number is used as
  // today; the India rule only blocks numbers that would create a new driver.
  const isValidMobile = /^\d{10}$/.test(phoneInput);
  const normalizedPhone = isValidMobile ? `+91${phoneInput}` : '';

  // Fallback: If shipment has driverId but no driverPhone in assignment, fetch driver overview
  const needsOverviewFetch = !initialPhoneDigits && !!shipment.driverId;
  const { data: driverOverview } = useQuery({
    queryKey: ['admin', 'users', shipment.driverId, 'overview'],
    queryFn: () => getUserOverview(shipment.driverId!),
    enabled: needsOverviewFetch,
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (driverOverview?.user) {
      setOverviewApplied(true);
      const rawPhone = driverOverview.user.phoneNumber || '';
      const digits = sanitizePhoneDigits(rawPhone);
      if (digits) {
        setPhoneInput((prev) => prev || digits);
      }
      if (driverOverview.user.fullName) {
        const name = driverOverview.user.fullName;
        setDriverName((prev) => prev || name);
      }
      const driverProf = driverOverview.profile as DriverProfileResponse | null;
      if (driverProf?.vehicleNumber) {
        const vNum = driverProf.vehicleNumber.toUpperCase();
        setVehicleReg((prev) => {
          if (!prev) {
            setIsVehicleAutoFilled(true);
            return vNum;
          }
          return prev;
        });
      }
    }
  }, [driverOverview]);

  // Lookup query with instant placeholderData when matching shipment assignment
  const isExistingPhoneLookup =
    !!initialPhoneDigits &&
    cleanDigits === initialPhoneDigits &&
    !!(existingAssignment?.driverName || existingAssignment?.driverId);

  const { data: lookupData, isLoading: isLookingUp } = useQuery<DriverLookupResponse>({
    queryKey: ['drivers', 'lookup', normalizedPhone],
    queryFn: () => lookupDriverByPhone(normalizedPhone),
    enabled: isValidMobile && normalizedPhone.length >= 10,
    staleTime: 60 * 1000,
    placeholderData: isExistingPhoneLookup
      ? {
          exists: true,
          driverId: existingAssignment?.driverId ?? undefined,
          fullName: existingAssignment?.driverName ?? undefined,
          recentVehicleNumber: existingAssignment?.vehicleRegistrationNumber ?? undefined,
        }
      : undefined,
  });

  // When lookup resolves with recent vehicle, auto-fill if vehicle is empty
  useEffect(() => {
    if (lookupData?.exists && lookupData.recentVehicleNumber) {
      const recent = lookupData.recentVehicleNumber.toUpperCase();
      setVehicleReg((prev) => {
        if (!prev) {
          setIsVehicleAutoFilled(true);
          return recent;
        }
        return prev;
      });
    }
  }, [lookupData]);

  const handleVehicleChange = (val: string) => {
    const upper = val.toUpperCase();
    setVehicleReg(upper);
    if (isVehicleAutoFilled && upper !== lookupData?.recentVehicleNumber?.toUpperCase()) {
      setIsVehicleAutoFilled(false);
    }
  };

  const handlePhoneInputChange = (national: string) => {
    setPhoneInput(national);
    setPhoneError(null);
    if (national !== initialPhoneDigits && !lookupData?.recentVehicleNumber) {
      setIsVehicleAutoFilled(false);
    }
  };

  const invalidateKeys = [
    ['admin', 'shipments', 'detail', shipment.id],
    ['admin', 'shipments', 'invites', shipment.id],
    ['admin', 'shipments', 'bids', shipment.id],
    ['admin', 'shipments'],
    ['admin', 'dashboard'],
  ];

  const directAssignMut = useMutationHook(
    () => {
      const customName = driverName.trim();
      const resolvedName = lookupData?.exists && lookupData.fullName
        ? lookupData.fullName
        : (customName || undefined);

      const parsedVehicleId = vehicleIdInput.trim() ? Number(vehicleIdInput.trim()) : undefined;

      return adminAssignDriver(shipment.id, {
        driverPhone: normalizedPhone,
        driverName: resolvedName,
        vehicleRegistrationNumber: vehicleReg.trim() ? vehicleReg.trim().toUpperCase() : undefined,
        vehicleId: Number.isFinite(parsedVehicleId) ? parsedVehicleId : undefined,
      });
    },
    {
      successMessage: existingAssignment
        ? 'Driver assignment & details updated successfully!'
        : 'Driver successfully assigned to shipment!',
      invalidateKeys,
      errorMessage: (err) => (isPhoneInvalidIndia(err) ? INDIAN_MOBILE_ERROR : err.message),
      onError: (err) => {
        if (isPhoneInvalidIndia(err)) setPhoneError(INDIAN_MOBILE_ERROR);
      },
      onSuccess: (data) => {
        const phone = normalizedPhone;
        const name = (lookupData?.exists && lookupData.fullName) || driverName.trim() || 'Driver';
        setAssignedData({
          phone,
          vehicle: vehicleReg.trim().toUpperCase(),
          name,
          link: data.inviteUrl,
          notReady: readinessMissing(data.driverReadiness),
        });
      },
    },
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setPhoneError(null);

    if (partnerBlocked) return;
    if (!phoneInput.trim()) {
      return setPhoneError('Enter the driver’s 10-digit mobile number.');
    }
    const isExistingDriver = !!lookupData?.exists;
    if (!isValidMobile || (!isExistingDriver && !isValidNewIndianMobile(phoneInput))) {
      return setPhoneError(INDIAN_MOBILE_ERROR);
    }

    directAssignMut.mutate(undefined);
  };

  const [copied, setCopied] = useState(false);
  const handleCopyLink = () => {
    if (!assignedData?.link) return;
    navigator.clipboard.writeText(assignedData.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const title = `${existingAssignment ? 'Update Driver Assignment' : 'Assign Driver'} • Shipment #${shipment.id}`;
  const formDirty = useIsDirty({ phoneInput, driverName, vehicleReg, vehicleIdInput }, overviewApplied);
  const formDismiss = useDialogDismiss({
    dirty: formDirty,
    busy: directAssignMut.isLoading || isLookingUp,
    onClose,
  });
  // Share screen: a stray click must not lose the trip link, but Done / Escape close at once.
  const doneDismiss = useDialogDismiss({ onClose });

  if (assignedData) {
    const cleanPhoneDigits = assignedData.phone.replace(/[^0-9]/g, '');
    const whatsappMessage = encodeURIComponent(
      `Hey! You have been assigned trip #${shipment.id} (${formatRoute(shipment)}) on DNM Logistics. ${
        assignedData.link ? `Open the driver app or tap: ${assignedData.link}` : 'Open the driver app.'
      }`,
    );
    const whatsappUrl = `https://wa.me/${cleanPhoneDigits}?text=${whatsappMessage}`;

    return (
      <DialogShell dismiss={doneDismiss} label={t('shipments.assignDriverModal.assignedLabel')} scrimClassName={styles.scrim} panelClassName={styles.panel} panelStyle={{ maxWidth: 440, textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--status-green-bg)', color: 'var(--status-green-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: 24 }}>
          ✓
        </div>
        <h3 className={styles.title} style={{ marginBottom: 6 }}>{t('shipments.assignDriverModal.assignedTitle')}</h3>
        <p className={styles.hint} style={{ marginBottom: 20 }}>
          {t('shipments.assignDriverModal.assignedTrip', { shipmentId: shipment.id, route: formatRoute(shipment) })}{' '}
          <strong>{assignedData.name}</strong>
          {t('shipments.assignDriverModal.assignedPhone', { phone: assignedData.phone })}
          <strong>{assignedData.vehicle || 'Not specified'}</strong>.
        </p>
        {assignedData.notReady.length > 0 && (
          <div className={kycStyles.readiness}>
            <Notice tone="warning" testId="assign-driver-not-ready">
              {t('shipments.assignDriverModal.driverNotReady', { items: assignedData.notReady.join(' / ') })}
            </Notice>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.submit}
            style={{
              backgroundColor: '#25D366',
              color: '#fff',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '10px 16px',
              borderRadius: 'var(--radius-button)',
              fontWeight: 600,
            }}
          >
            {t('shipments.assignDriverModal.shareWhatsApp')}
          </a>

          {assignedData.link && (
            <button
              type="button"
              className={styles.cancel}
              onClick={handleCopyLink}
              style={{ width: '100%', padding: '10px 16px', borderRadius: 'var(--radius-button)', fontWeight: 600 }}
            >
              {copied ? '✓ Copied Link to Clipboard' : 'Copy Trip Link'}
            </button>
          )}
        </div>

        <button type="button" className={styles.cancel} onClick={onClose} style={{ width: '100%', border: 'none', color: 'var(--muted)' }}>
          {t('shipments.assignDriverModal.done')}
        </button>
      </DialogShell>
    );
  }

  const isLoading = directAssignMut.isLoading || isLookingUp;
  const isCurrentlyAssigned =
    !!existingAssignment?.driverPhone &&
    cleanDigits === sanitizePhoneDigits(existingAssignment.driverPhone);

  const displayDriverName =
    (lookupData?.exists && lookupData.fullName) ||
    (isCurrentlyAssigned && existingAssignment?.driverName) ||
    'Registered Driver';

  return (
    <DialogShell dismiss={formDismiss} label={title} scrimClassName={styles.scrim} panelClassName={styles.panel} panelStyle={{ width: 500 }}>
      <DialogTitleRow context="assign-driver" title={title}>
        <h3 className={styles.title} style={{ marginBottom: 4 }}>
          {title}
        </h3>
      </DialogTitleRow>
      <p className={styles.hint} style={{ marginTop: 0, marginBottom: 14 }}>
        {formatRoute(shipment)}
        {shipment.fleetManagerId ? ` • Partner #${shipment.fleetManagerId}` : ''}
      </p>

      {/* Status / Existing Assignment Banner */}
      {existingAssignment ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            backgroundColor: 'var(--status-green-bg)',
            border: '1px solid var(--status-green-border)',
            borderRadius: 'var(--radius-button)',
            marginBottom: 16,
            fontSize: 12.5,
            color: 'var(--status-green-text)',
          }}
        >
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: 'var(--status-green-text)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 'bold',
              flexShrink: 0,
            }}
          >
            ✓
          </div>
          <div>
            <strong>{t('shipments.assignDriverModal.currentlyAssignedLabel')}</strong> {existingAssignment.driverName || 'Driver'} ({existingAssignment.driverPhone || 'No phone'})
            {existingAssignment.vehicleRegistrationNumber ? ` • ${existingAssignment.vehicleRegistrationNumber}` : ''}
            {existingAssignment.vehicleId ? ` • Vehicle #${existingAssignment.vehicleId}` : ''}
            {t('shipments.assignDriverModal.updateBelow')}
          </div>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            backgroundColor: 'var(--status-red-bg)',
            border: '1px solid var(--status-red-border)',
            borderRadius: 'var(--radius-button)',
            marginBottom: 16,
            fontSize: 12.5,
            color: 'var(--status-red-text-2)',
          }}
        >
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: 'var(--danger)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 'bold',
              flexShrink: 0,
            }}
          >
            ✓
          </div>
          <div>
            <strong>{t('shipments.assignDriverModal.awarded')}</strong> {t('shipments.assignDriverModal.awardedHint')}
          </div>
        </div>
      )}

      {existingAssignment && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 16,
            padding: 4,
            backgroundColor: 'var(--status-gray-bg)',
            borderRadius: 'var(--radius-button)',
          }}
        >
          <button
            type="button"
            style={{
              flex: 1,
              padding: '7px 12px',
              borderRadius: 'var(--radius-button)',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 12.5,
              background: activeMode === 'edit_details' ? '#fff' : 'transparent',
              color: activeMode === 'edit_details' ? 'var(--ink)' : 'var(--muted)',
              boxShadow: activeMode === 'edit_details' ? 'var(--shadow-card)' : 'none',
            }}
            onClick={() => {
              setActiveMode('edit_details');
              setError(null);
              setPhoneInput(initialPhoneDigits);
              setDriverName(initialName);
              setVehicleReg(initialVehicle);
              setVehicleIdInput(initialVehicleId);
            }}
          >
            {t('shipments.assignDriverModal.editDetails')}
          </button>
          <button
            type="button"
            style={{
              flex: 1,
              padding: '7px 12px',
              borderRadius: 'var(--radius-button)',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 12.5,
              background: activeMode === 'change_driver' ? '#fff' : 'transparent',
              color: activeMode === 'change_driver' ? 'var(--ink)' : 'var(--muted)',
              boxShadow: activeMode === 'change_driver' ? 'var(--shadow-card)' : 'none',
            }}
            onClick={() => {
              setActiveMode('change_driver');
              setError(null);
              setPhoneInput('');
              setDriverName('');
              setVehicleReg('');
              setVehicleIdInput('');
            }}
          >
            {t('shipments.assignDriverModal.changeDriver')}
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Driver Mobile Number with +91 Prefix Group */}
        <div>
          <label htmlFor="assign-driver-phone" className={styles.label} style={{ margin: '0 0 6px', fontWeight: 600, fontSize: 11, letterSpacing: '0.04em' }}>
            {t('shipments.assignDriverModal.phoneLabel')}
            <span style={{ color: 'var(--status-red-text)' }}>*</span>
          </label>
          <PhoneInput
            id="assign-driver-phone"
            placeholder={t('shipments.assignDriverModal.phonePlaceholder')}
            value={phoneInput}
            onValueChange={handlePhoneInputChange}
            autoFocus={!initialPhoneDigits}
            error={phoneError}
            trailing={
              isLookingUp ? (
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{t('shipments.assignDriverModal.lookingUp')}</span>
              ) : isValidMobile && lookupData?.exists ? (
                <span style={{ color: 'var(--status-green-text)', fontSize: 16, fontWeight: 'bold' }}>✓</span>
              ) : null
            }
          />
          <p className={styles.hint} style={{ marginTop: 4, fontSize: 11 }}>
            {t('shipments.assignDriverModal.phoneHint')}
          </p>
        </div>

        {/* Lookup Result State Cards (DNM Partner App Experience) */}
        {isValidMobile && lookupData && (
          <div style={{ marginTop: 14 }}>
            {lookupData.exists ? (
              // Existing Driver Profile Card with Initials Avatar & Badges
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  backgroundColor: '#fff',
                  border: '1px solid var(--status-green-border)',
                  borderRadius: 12,
                  boxShadow: 'var(--shadow-card)',
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    backgroundColor: 'var(--ink)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 14,
                    flexShrink: 0,
                  }}
                >
                  {getInitials(displayDriverName)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 14, color: 'var(--ink)' }}>
                      {displayDriverName}
                    </strong>
                    <span
                      style={{
                        backgroundColor: 'var(--status-green-bg)',
                        color: 'var(--status-green-text)',
                        fontSize: 10.5,
                        fontWeight: 700,
                        padding: '2px 7px',
                        borderRadius: 4,
                      }}
                    >
                      {t('shipments.assignDriverModal.dnmDriver')}
                    </span>
                    {isCurrentlyAssigned && (
                      <span
                        style={{
                          backgroundColor: 'var(--action-primary-soft)',
                          color: 'var(--accent)',
                          fontSize: 10.5,
                          fontWeight: 700,
                          padding: '2px 7px',
                          borderRadius: 4,
                        }}
                      >
                        {t('shipments.assignDriverModal.currentlyAssigned')}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                    +91 {cleanDigits}
                  </div>
                </div>
              </div>
            ) : (
              // New Driver Card (Friendly, non-intrusive)
              <div
                style={{
                  padding: '12px 14px',
                  backgroundColor: 'var(--surface)',
                  border: '1px solid var(--line)',
                  borderRadius: 12,
                }}
              >
                {!phoneError && (
                  <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>
                    {t('shipments.assignDriverModal.driverNotFound')}
                  </p>
                )}
                <label className={styles.label} style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 600 }}>
                  {t('shipments.assignDriverModal.nameLabel')}
                  <span style={{ color: 'var(--muted)', fontWeight: 'normal' }}>{t('shipments.assignDriverModal.nameOptional')}</span>
                </label>
                <input
                  className={styles.input}
                  placeholder={t('shipments.assignDriverModal.namePlaceholder')}
                  value={driverName}
                  onChange={(e) => setDriverName(e.target.value)}
                />
              </div>
            )}
          </div>
        )}

        {/* Vehicle Registration Number Section */}
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <label className={styles.label} style={{ margin: 0, fontWeight: 600, fontSize: 11, letterSpacing: '0.04em' }}>
              {t('shipments.assignDriverModal.vehicleRegLabel')}
              <span style={{ color: 'var(--muted)', fontWeight: 'normal' }}>{t('shipments.assignDriverModal.optional')}</span>
            </label>
            {isVehicleAutoFilled && (
              <span
                style={{
                  backgroundColor: 'var(--status-green-bg)',
                  color: 'var(--status-green-text)',
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '2px 6px',
                  borderRadius: 4,
                }}
              >
                {t('shipments.assignDriverModal.autoFilled')}
              </span>
            )}
          </div>
          <input
            className={styles.input}
            placeholder={t('shipments.assignDriverModal.vehicleRegPlaceholder')}
            value={vehicleReg}
            onChange={(e) => handleVehicleChange(e.target.value)}
            style={{ textTransform: 'uppercase' }}
          />
          <p className={styles.hint} style={{ marginTop: 4, fontSize: 11 }}>
            {t('shipments.assignDriverModal.vehicleRegHint')}
          </p>
        </div>

        {/* Vehicle ID Section (Optional database ID) */}
        <div style={{ marginTop: 12 }}>
          <label className={styles.label} style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 11, letterSpacing: '0.04em' }}>
            {t('shipments.assignDriverModal.vehicleIdLabel')}
            <span style={{ color: 'var(--muted)', fontWeight: 'normal' }}>{t('shipments.assignDriverModal.optional')}</span>
          </label>
          <input
            className={styles.input}
            placeholder={t('shipments.assignDriverModal.vehicleIdPlaceholder')}
            value={vehicleIdInput}
            onChange={(e) => setVehicleIdInput(e.target.value.replace(/[^0-9]/g, ''))}
          />
          <p className={styles.hint} style={{ marginTop: 4, fontSize: 11 }}>
            {t('shipments.assignDriverModal.vehicleIdHint')}
          </p>
        </div>

        {error && <div className={styles.error} style={{ marginTop: 12 }}>{error}</div>}

        {partnerBlocked && (
          <div className={kycStyles.blocker}>
            <Notice tone="danger" testId="assign-partner-kyc-blocker">
              {t('shipments.assignDriverModal.partnerKycBlocker')}{' '}
              {partnerGate.entry.userId != null && (
                <Link to={`/users/${partnerGate.entry.userId}`} state={{ from: `/shipments/${shipment.id}` }}>
                  {t('shipments.assignDriverModal.verifyPartner')}
                </Link>
              )}
            </Notice>
          </div>
        )}

        <div className={styles.actions} style={{ marginTop: 20 }}>
          <button type="button" className={styles.cancel} onClick={formDismiss.requestClose} disabled={isLoading}>
            {t('common.actions.cancel')}
          </button>
          <button
            type="submit"
            className={styles.submit}
            disabled={isLoading || !phoneInput || partnerBlocked}
          >
            {directAssignMut.isLoading
              ? 'Assigning…'
              : (existingAssignment ? 'Update Driver & Confirm Trip' : 'Assign Driver & Confirm Trip')}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

